import { useEffect, useMemo, useState } from 'react'
import type { PrioridadeTriagem } from '../../dominio/didatico/ocupantes'
import {
  VITIMAS_POR_ID,
} from '../../dominio/tabletop/atendimento'
import type { BlocoVitimaTabletop } from '../../dominio/tabletop/tipos'
import {
  IconeCena,
  IconeChevronBaixo,
  IconeChevronCima,
  IconeEstadoClinico,
  IconeFechar,
  IconeOculto,
  IconeVisivel,
} from '../icones'

export const ROTULOS_TRIAGEM: Record<PrioridadeTriagem, string> = {
  vermelho: 'Vermelho — crítico',
  amarelo: 'Amarelo — urgente',
  verde: 'Verde — leve',
  preto: 'Preto — óbito',
}

export interface CartaoCondicaoClinicaVitimaProps {
  readonly fichaId?: string
  readonly vitima?: BlocoVitimaTabletop
  readonly objetoId?: string
  readonly nomeVitima?: string
  readonly aoFechar?: () => void
  readonly modoFlutuante?: boolean
  readonly aoNavegarParaRoteiro?: () => void
}

/**
 * Cartão clínico de alta fidelidade para visualização no Tabletop e no Inspetor.
 * Segue diretrizes de design tático médico (design-taste-frontend):
 * - Dados iniciam sempre ocultos ("os dados da vítima devem começar ocultos")
 * - Grade de sinais vitais dinâmicos com aferição por toque
 * - Protocolo XABCDE estruturado em cartões semânticos por letra
 * - Balão de comunicação de queixa com a vítima
 * - Diretrizes de triagem e extração
 */
export function CartaoCondicaoClinicaVitima({
  fichaId,
  vitima,
  objetoId,
  nomeVitima,
  aoFechar,
  modoFlutuante,
  aoNavegarParaRoteiro,
}: CartaoCondicaoClinicaVitimaProps) {
  const idRealFicha = fichaId ?? vitima?.fichaAtendimentoId
  const ficha = idRealFicha ? VITIMAS_POR_ID[idRealFicha] : null
  const [revelados, setRevelados] = useState<ReadonlySet<string>>(() => new Set())
  const [minimizado, setMinimizado] = useState(false)

  // Sempre que a vítima selecionada ou sua ficha mudar, os dados clínicos começam ocultos
  useEffect(() => {
    setRevelados(new Set())
    setMinimizado(false)
  }, [idRealFicha, objetoId])

  if (!ficha) {
    return (
      <div className={`cartao-condicao-clinica cartao-condicao-clinica--vazio ${modoFlutuante ? 'cartao-condicao-clinica--flutuante' : ''}`}>
        {modoFlutuante && (
          <div className="cartao-condicao-clinica__header-flutuante">
            <div className="cartao-condicao-clinica__header-flutuante-esquerda">
              <span className="cartao-condicao-clinica__header-flutuante-icone">
                <IconeEstadoClinico tamanho={16} />
              </span>
              <span className="cartao-condicao-clinica__header-flutuante-titulo">
                {nomeVitima ? `${nomeVitima} • ` : ''}Condição Clínica
              </span>
            </div>
            <div className="cartao-condicao-clinica__header-flutuante-acoes">
              {aoNavegarParaRoteiro && (
                <button
                  type="button"
                  className="btn-acao-flutuante-roteiro"
                  onClick={aoNavegarParaRoteiro}
                  title="Abrir no Roteiro de Aula lateral"
                >
                  <IconeCena tamanho={13} />
                  <span>Roteiro</span>
                </button>
              )}
              {aoFechar && (
                <button
                  type="button"
                  className="btn-fechar-flutuante"
                  onClick={aoFechar}
                  title="Fechar painel clínico"
                  aria-label="Fechar"
                >
                  <IconeFechar tamanho={14} />
                </button>
              )}
            </div>
          </div>
        )}
        <div className="cartao-condicao-clinica__vazio-icone">
          <IconeEstadoClinico tamanho={26} />
        </div>
        <span className="cartao-condicao-clinica__tag">Nenhuma ficha clínica vinculada</span>
        <p className="cartao-condicao-clinica__instrucao">
          Vincule uma <strong>Ficha clínica</strong> para carregar o perfil de trauma, sinais vitais dinâmicos (Glasgow, PA, FC, SpO2, FR) e o protocolo XABCDE desta vítima.
        </p>
      </div>
    )
  }

  const { perfil, achadosIniciais, gabaritoInstrutor } = ficha
  const ecg = achadosIniciais.d_escala_glasgow.leitura ?? achadosIniciais.d_escala_glasgow.valor ?? '15'
  const pa = achadosIniciais.c_pressao_arterial.leitura ?? achadosIniciais.c_pressao_arterial.valor ?? 'N/I'
  const fc = achadosIniciais.c_pulso_radial.leitura ?? achadosIniciais.c_pulso_radial.valor ?? 'N/I'
  const spo2 = achadosIniciais.sinais_spo2.leitura ?? achadosIniciais.sinais_spo2.valor ?? 'N/I'
  const fr = achadosIniciais.b_frequencia_respiratoria.leitura ?? achadosIniciais.b_frequencia_respiratoria.valor ?? 'N/I'

  // Vitais estruturados com nomes completos, siglas e unidades
  const vitais = useMemo(() => [
    {
      id: 'sinal_pa',
      sigla: 'PA',
      nome: 'Pressão arterial',
      valor: pa.includes('mmHg') ? pa : `${pa} mmHg`,
      acaoExaminar: 'Aferir PA',
    },
    {
      id: 'sinal_fc',
      sigla: 'FC',
      nome: 'Frequência cardíaca',
      valor: fc.includes('bpm') ? fc : `${fc} bpm`,
      acaoExaminar: 'Palpar pulso',
    },
    {
      id: 'sinal_fr',
      sigla: 'FR',
      nome: 'Freq. respiratória',
      valor: fr.includes('irpm') || fr.includes('mrpm') ? fr : `${fr} irpm`,
      acaoExaminar: 'Aferir FR',
    },
    {
      id: 'sinal_spo2',
      sigla: 'SpO2',
      nome: 'Oximetria de pulso',
      valor: spo2.includes('%') ? spo2 : `${spo2}%`,
      acaoExaminar: 'Medir SpO2',
    },
    {
      id: 'sinal_glasgow',
      sigla: 'ECG',
      nome: 'Escala de Glasgow',
      valor: `${ecg} / 15`,
      destaque: true,
      acaoExaminar: 'Avaliar Glasgow',
    },
  ], [pa, fc, fr, spo2, ecg])

  // Etapas XABCDE disponíveis nesta vítima
  const etapasXABCDE = useMemo(() => [
    {
      id: 'xabcde_x',
      letra: 'X',
      titulo: 'Hemorragia Exsanguinante',
      subtitulo: 'Sangramentos ativos graves',
      achado: achadosIniciais.x_hemorragia_externa.valor,
      ativo: achadosIniciais.x_hemorragia_externa.estado !== 'nao_informado',
      classe: 'etapa-letra--x',
    },
    {
      id: 'xabcde_a',
      letra: 'A',
      titulo: 'Vias Aéreas e Coluna Cervical',
      subtitulo: 'Permeabilidade e estabilização',
      achado: achadosIniciais.a_permeabilidade_via_aerea.valor,
      ativo: achadosIniciais.a_permeabilidade_via_aerea.estado !== 'nao_informado',
      classe: 'etapa-letra--a',
    },
    {
      id: 'xabcde_b',
      letra: 'B',
      titulo: 'Boa Ventilação e Respiração',
      subtitulo: 'Mecânica e expansibilidade',
      achado: achadosIniciais.b_frequencia_respiratoria.valor,
      ativo: achadosIniciais.b_frequencia_respiratoria.estado !== 'nao_informado',
      classe: 'etapa-letra--b',
    },
    {
      id: 'xabcde_c',
      letra: 'C',
      titulo: 'Circulação e Perfusão',
      subtitulo: 'Pulsos, temperatura e choque',
      achado: achadosIniciais.c_pressao_arterial.valor,
      ativo: achadosIniciais.c_pressao_arterial.estado !== 'nao_informado',
      classe: 'etapa-letra--c',
    },
    {
      id: 'xabcde_d',
      letra: 'D',
      titulo: 'Déficit Neurológico',
      subtitulo: 'Nível de consciência e pupilas',
      achado: achadosIniciais.d_escala_glasgow.valor,
      ativo: achadosIniciais.d_escala_glasgow.estado !== 'nao_informado',
      classe: 'etapa-letra--d',
    },
    {
      id: 'xabcde_e',
      letra: 'E',
      titulo: 'Exposição e Dorso',
      subtitulo: 'Lesões corporais e temperatura',
      achado: achadosIniciais.e_exposicao_dorso.valor,
      ativo: achadosIniciais.e_exposicao_dorso.estado !== 'nao_informado',
      classe: 'etapa-letra--e',
    },
  ].filter((e) => e.ativo), [achadosIniciais])

  const todosIds = useMemo(() => {
    const ids: string[] = []
    if (perfil.condicaoDescrita) ids.push('resumo')
    vitais.forEach((v) => ids.push(v.id))
    etapasXABCDE.forEach((e) => ids.push(e.id))
    if (perfil.ator) ids.push('ator')
    if (gabaritoInstrutor) ids.push('diretriz')
    return ids
  }, [perfil, vitais, etapasXABCDE, gabaritoInstrutor])

  const todosRevelados = todosIds.length > 0 && todosIds.every((id) => revelados.has(id))

  const alternarItem = (chave: string) => {
    setRevelados((prev) => {
      const proximo = new Set(prev)
      if (proximo.has(chave)) proximo.delete(chave)
      else proximo.add(chave)
      return proximo
    })
  }

  const revelarTodos = () => setRevelados(new Set(todosIds))
  const ocultarTodos = () => setRevelados(new Set())

  return (
    <div className={`cartao-condicao-clinica ${modoFlutuante ? 'cartao-condicao-clinica--flutuante' : ''}`}>
      {/* 0. Barra Flutuante de Topo (quando em modo HUD flutuante no 3D) */}
      {modoFlutuante && (
        <div className="cartao-condicao-clinica__header-flutuante">
          <div className="cartao-condicao-clinica__header-flutuante-esquerda">
            <span className="cartao-condicao-clinica__header-flutuante-icone">
              <IconeEstadoClinico tamanho={16} />
            </span>
            <span className="cartao-condicao-clinica__header-flutuante-titulo">
              {nomeVitima ? `${nomeVitima} • ` : ''}Condição Clínica
            </span>
          </div>
          <div className="cartao-condicao-clinica__header-flutuante-acoes">
            {aoNavegarParaRoteiro && (
              <button
                type="button"
                className="btn-acao-flutuante-roteiro"
                onClick={aoNavegarParaRoteiro}
                title="Abrir no Roteiro de Aula lateral"
              >
                <IconeCena tamanho={13} />
                <span>Roteiro</span>
              </button>
            )}
            <button
              type="button"
              className="btn-minimizar-flutuante"
              onClick={() => setMinimizado((v) => !v)}
              title={minimizado ? 'Expandir cartão clínico' : 'Minimizar cartão clínico'}
              aria-label={minimizado ? 'Expandir' : 'Minimizar'}
            >
              {minimizado ? <IconeChevronCima tamanho={14} /> : <IconeChevronBaixo tamanho={14} />}
            </button>
            {aoFechar && (
              <button
                type="button"
                className="btn-fechar-flutuante"
                onClick={aoFechar}
                title="Fechar cartão clínico"
                aria-label="Fechar"
              >
                <IconeFechar tamanho={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* 1. Cabeçalho de Identificação Clínica */}
      <div className="cartao-condicao-clinica__header">
        <div className="cartao-condicao-clinica__titulos">
          <span className="cartao-condicao-clinica__papel">{perfil.papel}</span>
          <h4 className="cartao-condicao-clinica__nome">{ficha.titulo}</h4>
        </div>
        {gabaritoInstrutor && (
          revelados.has('diretriz') ? (
            <span className={`badge-triagem-chip badge-triagem-chip--${gabaritoInstrutor.triagemSugerida}`}>
              {ROTULOS_TRIAGEM[gabaritoInstrutor.triagemSugerida] ?? gabaritoInstrutor.triagemSugerida}
            </span>
          ) : (
            <button
              type="button"
              className="badge-triagem-chip badge-triagem-chip--oculta"
              onClick={() => alternarItem('diretriz')}
              title="Clique para conferir a triagem do gabarito"
            >
              <IconeOculto /> Triagem oculta
            </button>
          )
        )}
      </div>

      {!minimizado && (
        <>
          {/* 2. Barra de Controle e Ações Rápidas */}
          <div className="cartao-condicao-clinica__barra-revelacao">
            <div className="cartao-condicao-clinica__progresso-info">
              <span
                className="cartao-condicao-clinica__status-dot"
                data-status={revelados.size === 0 ? 'pendente' : todosRevelados ? 'completo' : 'em-andamento'}
              />
              <span className="cartao-condicao-clinica__progresso-texto">
                {revelados.size === 0
                  ? 'Exame clínico pendente'
                  : todosRevelados
                  ? 'Exame clínico completo'
                  : `${revelados.size} de ${todosIds.length} achados examinados`}
              </span>
            </div>
            <div className="cartao-condicao-clinica__acoes-globais">
              {revelados.size > 0 && (
                <button
                  type="button"
                  className="btn-acao-exame btn-acao-exame--ocultar"
                  onClick={ocultarTodos}
                  title="Ocultar todos os achados clínicos"
                >
                  <IconeOculto /> Ocultar
                </button>
              )}
              {!todosRevelados && (
                <button
                  type="button"
                  className="btn-acao-exame btn-acao-exame--revelar"
                  onClick={revelarTodos}
                  title="Examinar e revelar todos os achados clínicos"
                >
                  <IconeVisivel /> Revelar todos
                </button>
              )}
            </div>
          </div>

          {/* 3. Diagnóstico / Resumo Clínico Inicial */}
          {perfil.condicaoDescrita && (
            <div className="bloco-clinico bloco-clinico--resumo">
              <div className="bloco-clinico__cabecalho">
                <span className="bloco-clinico__titulo">Diagnóstico e Resumo Inicial</span>
                <button
                  type="button"
                  className="btn-toggle-etapa"
                  onClick={() => alternarItem('resumo')}
                  title={revelados.has('resumo') ? 'Ocultar diagnóstico' : 'Examinar diagnóstico'}
                >
                  {revelados.has('resumo') ? <><IconeVisivel /> Ocultar</> : <><IconeOculto /> Revelar</>}
                </button>
              </div>
              {revelados.has('resumo') ? (
                <p className="cartao-condicao-clinica__resumo">{perfil.condicaoDescrita}</p>
              ) : (
                <p className="cartao-condicao-clinica__resumo cartao-condicao-clinica__resumo--oculto">
                  Diagnóstico inicial oculto. Realize o exame físico ou clique em "Revelar".
                </p>
              )}
            </div>
          )}

          {/* 4. Sinais Vitais Dinâmicos (Grade Tática) */}
          <div className="bloco-clinico">
            <div className="bloco-clinico__cabecalho">
              <span className="bloco-clinico__titulo">Sinais Vitais Dinâmicos</span>
              <span className="bloco-clinico__dica">Toque no card para aferir</span>
            </div>
            <div className="grade-sinais-vitais" role="group" aria-label="Sinais vitais da vítima">
              {vitais.map((vital) => {
                const revelado = revelados.has(vital.id)
                return (
                  <button
                    key={vital.id}
                    type="button"
                    className={`card-sinal-vital ${revelado ? 'card-sinal-vital--revelado' : 'card-sinal-vital--oculto'} ${vital.destaque ? 'card-sinal-vital--destaque' : ''}`}
                    onClick={() => alternarItem(vital.id)}
                    title={revelado ? `Ocultar ${vital.nome}` : `${vital.acaoExaminar}: clique para aferir`}
                  >
                    <div className="card-sinal-vital__topo">
                      <span className="card-sinal-vital__sigla">{vital.sigla}</span>
                      <span className="card-sinal-vital__nome">{vital.nome}</span>
                      <span className="card-sinal-vital__icone">
                        {revelado ? <IconeVisivel /> : <IconeOculto />}
                      </span>
                    </div>
                    <div className="card-sinal-vital__corpo">
                      {revelado ? (
                        <span className="card-sinal-vital__valor">{vital.valor}</span>
                      ) : (
                        <span className="card-sinal-vital__valor-oculto">•••</span>
                      )}
                    </div>
                    <div className="card-sinal-vital__rodape">
                      <span className="card-sinal-vital__acao">
                        {revelado ? 'Aferido' : vital.acaoExaminar}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* 5. Avaliação Primária XABCDE */}
          {etapasXABCDE.length > 0 && (
            <div className="bloco-clinico">
              <div className="bloco-clinico__cabecalho">
                <span className="bloco-clinico__titulo">Avaliação do Trauma (XABCDE)</span>
                <span className="bloco-clinico__dica">
                  {etapasXABCDE.filter((e) => revelados.has(e.id)).length} de {etapasXABCDE.length} examinados
                </span>
              </div>
              <div className="lista-etapas-xabcde">
                {etapasXABCDE.map((etapa) => {
                  const revelado = revelados.has(etapa.id)
                  return (
                    <div
                      key={etapa.id}
                      className={`card-etapa-xabcde ${revelado ? 'card-etapa-xabcde--revelado' : 'card-etapa-xabcde--oculto'}`}
                    >
                      <div className="card-etapa-xabcde__letra-col">
                        <span className={`badge-letra-xabcde ${etapa.classe}`}>{etapa.letra}</span>
                      </div>
                      <div className="card-etapa-xabcde__conteudo">
                        <div className="card-etapa-xabcde__titulos">
                          <span className="card-etapa-xabcde__titulo">{etapa.titulo}</span>
                          <span className="card-etapa-xabcde__subtitulo">{etapa.subtitulo}</span>
                        </div>
                        {revelado ? (
                          <div className="card-etapa-xabcde__achado">
                            <span className="card-etapa-xabcde__achado-texto">{etapa.achado}</span>
                          </div>
                        ) : (
                          <div className="card-etapa-xabcde__pendente">
                            <span className="card-etapa-xabcde__pendente-texto">Aguardando exame</span>
                          </div>
                        )}
                      </div>
                      <div className="card-etapa-xabcde__acao-col">
                        <button
                          type="button"
                          className={`btn-acao-etapa ${revelado ? 'btn-acao-etapa--revelado' : 'btn-acao-etapa--examinar'}`}
                          onClick={() => alternarItem(etapa.id)}
                          title={revelado ? `Ocultar etapa ${etapa.letra}` : `Examinar etapa ${etapa.letra}`}
                        >
                          {revelado ? (
                            <>
                              <IconeVisivel />
                              <span>Ocultar</span>
                            </>
                          ) : (
                            <>
                              <IconeOculto />
                              <span>Examinar</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* 6. Comunicação com a Vítima (Fala do Ator) */}
          {perfil.ator && (
            <div className="bloco-clinico bloco-clinico--comunicacao">
              <div className="bloco-clinico__cabecalho">
                <span className="bloco-clinico__titulo">Comunicação com a Vítima</span>
                <button
                  type="button"
                  className="btn-toggle-etapa"
                  onClick={() => alternarItem('ator')}
                  title={revelados.has('ator') ? 'Ocultar fala da vítima' : 'Ouvir queixa da vítima'}
                >
                  {revelados.has('ator') ? <><IconeVisivel /> Ocultar</> : <><IconeOculto /> Ouvir queixa</>}
                </button>
              </div>
              {revelados.has('ator') ? (
                <div className="balao-fala-vitima">
                  <span className="balao-fala-vitima__aspas">"</span>
                  <p className="balao-fala-vitima__texto">{perfil.ator}</p>
                </div>
              ) : (
                <p className="balao-fala-vitima__placeholder">
                  Aproxime-se e pergunte: "Como você está se sentindo? Onde dói?" ou clique em "Ouvir queixa".
                </p>
              )}
            </div>
          )}

          {/* 7. Gabarito do Instrutor (Triagem & Extração) */}
          {gabaritoInstrutor && (
            <div className="bloco-clinico bloco-clinico--gabarito">
              <div className="bloco-clinico__cabecalho">
                <span className="bloco-clinico__titulo">Gabarito do Instrutor</span>
                <button
                  type="button"
                  className="btn-toggle-etapa"
                  onClick={() => alternarItem('diretriz')}
                  title={revelados.has('diretriz') ? 'Ocultar gabarito' : 'Conferir gabarito oficial'}
                >
                  {revelados.has('diretriz') ? <><IconeVisivel /> Ocultar</> : <><IconeOculto /> Conferir</>}
                </button>
              </div>
              {revelados.has('diretriz') ? (
                <div className="conteudo-gabarito">
                  <div className="conteudo-gabarito__linha">
                    <span className="conteudo-gabarito__rotulo">Prioridade de Extração:</span>
                    <span className="conteudo-gabarito__badge-extracao">
                      {gabaritoInstrutor.prioridadeExtracao.toUpperCase()}
                    </span>
                  </div>
                  <p className="conteudo-gabarito__justificativa">
                    {gabaritoInstrutor.justificativaTriagem}
                  </p>
                </div>
              ) : (
                <p className="conteudo-gabarito__placeholder">
                  Gabarito oficial de extração e triagem reservado para conferência após atendimento.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
